// The person at the foot of the sidebar and their account menu (Console D): profile, preferences
// (the language lives there, saved on the profile, D63), settings, theme, keyboard shortcuts,
// docs, switching organization and signing out. The menu opens above the person.
import { useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import { useId, useState } from 'react'
import { signOut } from '../../api/queries'
import type { Me } from '../../api/types'
import { useI18n } from '../../i18n'
import { consoleAnalytics } from '../../lib/analytics'
import { initials } from '../../lib/format'
import { readTheme, saveTheme, type Theme } from '../../lib/theme'
import { leaveSandbox } from '../../sandbox'
import { Seg } from '../d'
import { Icon } from '../icons'
import { usePopover } from './usePopover'

export const LOCALE_NAMES: Record<string, string> = { en: 'English', 'pt-BR': 'Português (Brasil)' }
export const DOCS_URL = 'https://docs.apsio.io'

const ROLE_KEY = { owner: 'role.owner', admin: 'role.admin', member: 'role.member' } as const

export function AccountMenu({
  me,
  orgId,
  projectId,
  onKeys,
}: {
  me: Me
  orgId: string
  projectId: string
  onKeys: () => void
}) {
  const { t } = useI18n()
  const pop = usePopover('above')
  const popId = useId()
  const [theme, setTheme] = useState<Theme>(readTheme)
  const qc = useQueryClient()
  const navigate = useNavigate()
  const org = me.orgs.find((o) => o.id === orgId)
  const name = me.user.name || me.user.email
  const sign = initials(me.user.name, me.user.email)
  return (
    <div className="nav-me">
      <button
        ref={pop.anchor}
        type="button"
        className="nav-user"
        aria-expanded={pop.open}
        aria-controls={popId}
        title={name}
        onClick={pop.toggle}
      >
        <span className="avatar" aria-hidden="true">
          {sign}
        </span>
        <span className="who">
          <b>{name}</b>
          {org ? <span>{`${t(ROLE_KEY[org.role])} · ${org.name}`}</span> : null}
        </span>
        <Icon name="updown" small />
      </button>
      {pop.open ? (
        <section
          ref={pop.pop}
          id={popId}
          className="pop me-pop"
          style={pop.style}
          aria-label={t('user.menu')}
        >
          <div className="me-h">
            <span className="avatar" aria-hidden="true">
              {sign}
            </span>
            <span className="who">
              <b>{name}</b>
              <span translate="no">{me.user.email}</span>
            </span>
          </div>
          <hr />
          <Link
            className="pal-i"
            to="/o/$orgId/p/$projectId/settings/$section"
            params={{ orgId, projectId, section: 'profile' }}
            onClick={() => pop.close()}
          >
            <Icon name="user" />
            <span>{t('user.profile')}</span>
          </Link>
          <Link
            className="pal-i"
            to="/o/$orgId/p/$projectId/settings/$section"
            params={{ orgId, projectId, section: 'preferences' }}
            onClick={() => pop.close()}
          >
            <Icon name="sliders" />
            <span>{t('settings.tab.preferences')}</span>
          </Link>
          <Link
            className="pal-i"
            to="/o/$orgId/p/$projectId/settings/$section"
            params={{ orgId, projectId, section: 'general' }}
            onClick={() => pop.close()}
          >
            <Icon name="settings" />
            <span>{t('nav.settings')}</span>
            <span className="k2" aria-hidden="true" translate="no">
              <kbd>g</kbd> <kbd>,</kbd>
            </span>
          </Link>
          <div className="me-row">
            <span>{t('user.theme')}</span>
            <Seg
              label={t('user.theme')}
              value={theme}
              onChange={(v) => {
                setTheme(v)
                saveTheme(v)
              }}
              options={[
                { value: 'system', label: t('user.theme.systemShort') },
                { value: 'light', label: t('user.theme.light') },
                { value: 'dark', label: t('user.theme.dark') },
              ]}
            />
          </div>
          <hr />
          <button
            type="button"
            className="pal-i"
            aria-haspopup="dialog"
            onClick={() => {
              pop.close()
              onKeys()
            }}
          >
            <Icon name="kbd" />
            <span>{t('keys.title')}</span>
            <span className="k2" aria-hidden="true">
              <kbd>?</kbd>
            </span>
          </button>
          <a className="pal-i" href={DOCS_URL} target="_blank" rel="noreferrer">
            <Icon name="book" />
            <span>{t('user.docs')}</span>
            <Icon name="ext" small />
          </a>
          {me.orgs.length > 1 ? (
            <>
              <p className="pal-g">{t('shell.switchOrg')}</p>
              {me.orgs.map((o) => (
                <Link
                  key={o.id}
                  className={o.id === orgId ? 'pal-i on' : 'pal-i'}
                  to="/o/$orgId"
                  params={{ orgId: o.id }}
                  onClick={() => pop.close()}
                >
                  <Icon name="org" />
                  <span>{o.name}</span>
                </Link>
              ))}
            </>
          ) : null}
          <hr />
          <button
            type="button"
            className="pal-i"
            onClick={async () => {
              const url = await signOut().catch(() => null)
              qc.clear()
              // The sandbox's token goes with the session (D105).
              leaveSandbox()
              // The next person on this browser starts anonymous in PostHog (plan step 2.4).
              consoleAnalytics()?.reset()
              if (url) window.location.assign(url)
              else navigate({ to: '/sign-in' })
            }}
          >
            <Icon name="out" />
            <span>{t('user.signOut')}</span>
          </button>
        </section>
      ) : null}
    </div>
  )
}
